/* eslint-disable no-console */
import NotificationSubscriptions from '../api/notificationSubscription';
import { useAlert } from 'dashboard/composables';

const PUSH_SUBSCRIPTION_TIMEOUT = 15000;
const PUSH_SUBSCRIPTION_TIMEOUT_MESSAGE =
  'Push notification setup timed out. Please check your browser notification settings and network connection.';
const BROWSER_PUSH_UNSUPPORTED_MESSAGE =
  'This browser does not support desktop notification';

const withTimeout = promise =>
  Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => {
        const error = new Error('Push subscription request timed out');
        error.name = 'PushSubscriptionTimeoutError';
        reject(error);
      }, PUSH_SUBSCRIPTION_TIMEOUT);
    }),
  ]);

export const verifyServiceWorkerExistence = (callback = () => {}) => {
  if (!('serviceWorker' in navigator)) {
    // Service Worker isn't supported on this browser, disable or hide UI.
    return;
  }

  if (!('PushManager' in window)) {
    // Push isn't supported on this browser, disable or hide UI.
    return;
  }

  navigator.serviceWorker
    .register('/sw.js', { updateViaCache: 'none' })
    .then(registration => callback(registration))
    .catch(registrationError => {
      // eslint-disable-next-line
      console.log('SW registration failed: ', registrationError);
    });
};

export const hasPushPermissions = () => {
  if ('Notification' in window) {
    return Notification.permission === 'granted';
  }
  return false;
};

const generateKeys = str =>
  btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

export const getPushSubscriptionPayload = subscription => ({
  subscription_type: 'browser_push',
  subscription_attributes: {
    endpoint: subscription.endpoint,
    p256dh: generateKeys(subscription.getKey('p256dh')),
    auth: generateKeys(subscription.getKey('auth')),
  },
});

export const sendRegistrationToServer = subscription => {
  return NotificationSubscriptions.create({
    notification_subscription: getPushSubscriptionPayload(subscription),
  });
};

export const registerSubscription = async (onSuccess = () => {}) => {
  if (!window.chatwootConfig?.vapidPublicKey) {
    return false;
  }

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    useAlert(BROWSER_PUSH_UNSUPPORTED_MESSAGE);
    return false;
  }

  try {
    const serviceWorkerRegistration = await navigator.serviceWorker.register(
      '/sw.js',
      { updateViaCache: 'none' }
    );
    const existingSubscription =
      await serviceWorkerRegistration.pushManager.getSubscription();
    const subscription =
      existingSubscription ||
      (await withTimeout(
        serviceWorkerRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: window.chatwootConfig.vapidPublicKey,
        })
      ));

    await sendRegistrationToServer(subscription);
    onSuccess();
    return true;
  } catch (error) {
    const isSubscriptionTimeout =
      error.name === 'PushSubscriptionTimeoutError';
    const message = isSubscriptionTimeout
      ? PUSH_SUBSCRIPTION_TIMEOUT_MESSAGE
      : BROWSER_PUSH_UNSUPPORTED_MESSAGE;
    // eslint-disable-next-line no-console
    console[isSubscriptionTimeout ? 'warn' : 'error'](
      'Push subscription registration failed:',
      error
    );
    useAlert(message);
    return false;
  }
};

export const requestPushPermissions = async ({ onSuccess }) => {
  if (!('Notification' in window)) {
    // eslint-disable-next-line no-console
    console.warn('Notification is not supported');
    useAlert(BROWSER_PUSH_UNSUPPORTED_MESSAGE);
    return false;
  }

  if (Notification.permission === 'denied') {
    useAlert(BROWSER_PUSH_UNSUPPORTED_MESSAGE);
    return false;
  }

  let permission = Notification.permission;

  if (permission !== 'granted') {
    try {
      permission = await Notification.requestPermission();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Notification permission request failed:', error);
      useAlert(BROWSER_PUSH_UNSUPPORTED_MESSAGE);
      return false;
    }
  }

  if (permission === 'granted') {
    return registerSubscription(onSuccess);
  }

  return false;
};
